import { useRef } from 'react';
import { useIntl } from 'react-intl';
import { Skeleton, Stack, Tooltip, Typography } from '@rossum/ui/material';
import { GridRenderCellParams } from '@rossum/ui/x-data-grid-pro';
import { createNewFilterQuery } from '../../mql/transformers';
import { StyledLink } from './StyledLink';

const TruncatedLink = ({ name, to }: { name: string; to: string }) => {
  const wrapperEl = useRef<HTMLDivElement>(null);
  const textEl = useRef<HTMLDivElement>(null);

  const shouldRenderTooltip =
    wrapperEl.current && textEl.current
      ? wrapperEl.current.clientWidth < textEl.current.scrollWidth
      : false;

  return (
    <Tooltip title={shouldRenderTooltip ? name : ''} enterDelay={600}>
      <Stack ref={wrapperEl} maxWidth="100%">
        <Typography
          component="div"
          sx={{
            fontSize: 'inherit',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: '100%',
          }}
          ref={textEl}
        >
          <StyledLink
            to={to}
            color="text.secondary"
            target="_blank"
            rel="noopener noreferrer"
          >
            {name}
          </StyledLink>
        </Typography>
      </Stack>
    </Tooltip>
  );
};

const LoadingSkeleton = () => {
  const { current: width } = useRef(Math.floor(Math.random() * 20) + 60);
  return <Skeleton width={`${width}%`} />;
};

type WorkspaceAndQueueProps = {
  queueId: number;
  queueName: string;
  workspaceName: string;
};

const WorkspaceAndQueue = ({
  queueId,
  queueName,
  workspaceName,
}: WorkspaceAndQueueProps) => {
  const query = createNewFilterQuery([
    {
      field: 'queue',
      operator: 'isAnyOf',
      value: [queueId],
    },
  ]);

  return (
    <Stack width="100%" height="100%" justifyContent="center">
      <TruncatedLink
        to={`/documents?filtering=${encodeURIComponent(query)}&level=queue`}
        name={[workspaceName, queueName].join(' > ')}
      />
    </Stack>
  );
};

type WorkspacesWithQueuesMap = Record<
  string,
  {
    url: string;
    queueId: number;
    queueName: string;
    workspaceName: string;
  }
>;

type WorkspacesQueuesCellProps = GridRenderCellParams<
  Record<never, never>,
  string
> & {
  workspacesProps: {
    workspacesWithQueuesMap: WorkspacesWithQueuesMap;
    workspacesAreLoading: boolean;
  };
};

const WorkspacesQueuesCell = ({
  value,
  workspacesProps,
}: WorkspacesQueuesCellProps) => {
  const intl = useIntl();
  const { workspacesAreLoading, workspacesWithQueuesMap } = workspacesProps;
  const data = workspacesWithQueuesMap[value || ''];

  if (workspacesAreLoading) {
    return (
      <Stack width="100%" height="100%" justifyContent="center">
        <LoadingSkeleton />
      </Stack>
    );
  }

  if (!data) {
    return (
      <Stack width="100%" height="100%" justifyContent="center">
        {intl.formatMessage({ id: 'components.duplicates.unknownQueue' })}
      </Stack>
    );
  }

  return (
    <WorkspaceAndQueue
      queueId={data.queueId}
      queueName={data.queueName}
      workspaceName={data.workspaceName}
    />
  );
};

export { WorkspaceAndQueue, WorkspacesQueuesCell };
export type { WorkspacesWithQueuesMap };
